import { Form, Input, Select } from "antd";
import { useEffect } from "react";
import { useStaffList } from "@/modules/staff/hooks/useStaff";
import { TerritorySelect } from "@/modules/territories/components/TerritorySelect";
import { FormModal } from "@/shared/components/FormModal";
import { useCreateBrigade, useUpdateBrigade } from "../hooks/useBrigade";
import { useBrigadeStore } from "../store/brigade.store";
import type { Brigade, CreateBrigadeRequest, UpdateBrigadeRequest } from "../types";

interface BrigadeFormModalProps {
	organizationId: string;
}

/** Builds a minimal PATCH payload: only the fields that actually changed. */
function buildUpdatePayload(values: CreateBrigadeRequest, editingItem: Brigade) {
	const updateData: UpdateBrigadeRequest = {};
	if (values.name !== editingItem.name) {
		updateData.name = values.name;
	}
	if (values.leaderId !== editingItem.leaderId) {
		updateData.leaderId = values.leaderId;
	}
	// Compare as sets so reordering is not treated as a change; a cleared
	// multi-select (undefined) becomes [] and unassigns all territories.
	const nextTerritoryIds = values.territoryIds ?? [];
	const prevTerritoryIds = (editingItem.territories ?? []).map((t) => t.id);
	const sameTerritories =
		nextTerritoryIds.length === prevTerritoryIds.length &&
		nextTerritoryIds.every((id) => prevTerritoryIds.includes(id));
	if (!sameTerritories) {
		updateData.territoryIds = nextTerritoryIds;
	}
	return updateData;
}

export function BrigadeFormModal({ organizationId }: BrigadeFormModalProps) {
	const [form] = Form.useForm<CreateBrigadeRequest>();
	const { modalOpen, editingItem, defaultOrganizationId, closeModal } = useBrigadeStore();

	const orgId = editingItem?.organizationId ?? defaultOrganizationId ?? organizationId;

	const { data: staffData } = useStaffList({
		organizationId: orgId ?? "",
		type: "BRIGADIER",
		page: 1,
		limit: 10,
	});
	const staffItems = staffData?.data?.items ?? [];

	const { mutate: createBrigade, isPending: isCreating } = useCreateBrigade();
	const { mutate: updateBrigade, isPending: isUpdating } = useUpdateBrigade();

	useEffect(() => {
		if (editingItem) {
			form.setFieldsValue({
				name: editingItem.name,
				leaderId: editingItem.leaderId,
				territoryIds: (editingItem.territories ?? []).map((t) => t.id),
				organizationId: editingItem.organizationId,
			});
		} else {
			form.resetFields();
			form.setFieldsValue({ organizationId: orgId ?? undefined });
		}
	}, [editingItem, orgId, form]);

	const handleClose = () => {
		closeModal();
		form.resetFields();
	};

	const handleSubmit = () => {
		form.validateFields().then((values) => {
			if (editingItem) {
				const updateData = buildUpdatePayload(values, editingItem);
				updateBrigade({ id: editingItem.id, data: updateData }, { onSuccess: handleClose });
			} else {
				createBrigade(
					{
						name: values.name,
						leaderId: values.leaderId,
						territoryIds: values.territoryIds ?? [],
						organizationId: values.organizationId ?? organizationId,
					},
					{ onSuccess: handleClose }
				);
			}
		});
	};

	return (
		<FormModal
			open={modalOpen}
			editing={!!editingItem}
			title="Yangi brigada yaratish"
			editTitle="Brigadani tahrirlash"
			onCancel={handleClose}
			onOk={handleSubmit}
			confirmLoading={isCreating || isUpdating}
			width={720}
		>
			<Form form={form} layout="vertical" requiredMark={false} className="mt-4">
				<Form.Item
					name="name"
					label="Brigada nomi"
					rules={[
						{ required: true, message: "Brigada nomini kiriting" },
						{ min: 1, max: 255, message: "1–255 belgi oralig'ida bo'lishi kerak" },
					]}
				>
					<Input placeholder="Masalan: 1-brigada" />
				</Form.Item>

				<Form.Item
					name="leaderId"
					label="Brigada rahbari"
					rules={[{ required: true, message: "Brigada rahbarini tanlang" }]}
				>
					<Select
						placeholder="Xodimni tanlang"
						showSearch
						filterOption={(input, option) =>
							(option?.label as string)?.toLowerCase().includes(input.toLowerCase())
						}
						options={staffItems.map((s) => ({
							value: s.id,
							label: `${s.fullname}${s.phoneNumber ? ` (${s.phoneNumber})` : ""}`,
						}))}
					/>
				</Form.Item>

				<Form.Item name="territoryIds" label="Hududlar">
					<TerritorySelect
						organizationId={orgId ?? undefined}
						multiple
						placeholder="Hududlarni tanlang"
						allowClear
					/>
				</Form.Item>

				{!editingItem && (
					<Form.Item name="organizationId" hidden>
						<Input />
					</Form.Item>
				)}
			</Form>
		</FormModal>
	);
}
